import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { OrganizationSchema, WebsiteSchema } from '../components/seo';
import PageSEO from '../components/seo/PageSEO';
import usePageContent from '../hooks/usePageContent';
import { getNecrologioUrl } from '../utils/necrologioUrl';
import PodcastBadges from '../components/podcast/PodcastBadges';

import { API_URL } from '../utils/apiUrl';

const HomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { t: cms } = usePageContent('homepage');
  const [searchName, setSearchName] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [recentObituaries, setRecentObituaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_condoglianze: 1234 });

  
  useEffect(() => {
    fetchRecentObituaries();
    fetchStats();
   
  }, []);

  const fetchRecentObituaries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/necrologi?limit=4`);
      setRecentObituaries(response.data);
    } catch (error) {
      console.error('Error fetching necrologi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats/necrologi`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchName) params.append('nome', searchName);
    if (searchLocation) params.append('comune', searchLocation);
    navigate(`/necrologi?${params.toString()}`);
  };

  return (
    <main className="bg-background-light">
      {/* SEO */}
      <PageSEO
        title="Necrologi Online e Commemorazioni Digitali"
        description="Resta in Pace è il portale italiano per necrologi online, commemorazioni digitali e supporto al lutto. Pubblica un necrologio, lascia un ricordo, accendi una candela virtuale."
        path="/"
        keywords="necrologi online, necrologi italia, lascito digitale, candele virtuali, servizi funebri, onoranze funebri, commemorazione, condoglianze, donazioni famiglia defunto, anniversario morte"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Resta in Pace",
          "url": "https://www.restainpace.it",
          "description": "Portale italiano per necrologi, lascito digitale e servizi funebri",
          "potentialAction": { "@type": "SearchAction", "target": "https://www.restainpace.it/necrologi?q={search_term_string}", "query-input": "required name=search_term_string" }
        }}
      />
      <OrganizationSchema />
      <WebsiteSchema />
      
      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[550px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Hero background image: srcset responsive (640w mobile / 1280w desktop) per ridurre payload mobile.
            fetchPriority='high' + matching preload in index.html garantisce LCP rapido. */}
        <img
          src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1280&q=75&fm=webp&auto=format"
          srcSet="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=640&q=70&fm=webp&auto=format 640w, https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1024&q=72&fm=webp&auto=format 1024w, https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1280&q=75&fm=webp&auto=format 1280w"
          sizes="100vw"
          alt=""
          fetchPriority="high"
          decoding="async"
          width="1280"
          height="550"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'sepia(0.3) contrast(0.9) brightness(0.8)' }}
        />
        <div className="hero-overlay absolute inset-0 bg-gradient-to-b from-espresso/40 to-espresso/60" aria-hidden="true"></div>
        
        <div className="hero-content relative z-10 w-full max-w-4xl px-6 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-display font-medium mb-3 italic">
            {cms('hero_title', t('homepage.heroTitle'))}
          </h1>
          <p className="text-sm md:text-base uppercase tracking-[0.18em] text-white/80 mb-6 font-light" data-testid="hero-seo-subtitle">
            Necrologi Online · Commemorazioni Digitali · Supporto al Lutto
          </p>
          <p className="text-lg md:text-xl mb-10 opacity-90 font-light">
            {cms('hero_subtitle', t('homepage.heroSubtitle'))}
          </p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="bg-white p-2 rounded-xl shadow-2xl flex flex-col md:flex-row gap-2 max-w-3xl mx-auto" data-testid="search-bar">
            <div className="flex-1 flex items-center px-4 gap-3 border-b md:border-b-0 md:border-r border-terra-siena/20 py-2">
              <span className="material-symbols-outlined text-terra-siena">person_search</span>
              <input 
                className="w-full bg-transparent border-none focus:ring-0 text-espresso placeholder-terra-siena/60 py-2 focus:outline-none"
                placeholder={t('homepage.searchPlaceholder')}
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                data-testid="search-name-input"
              />
            </div>
            <div className="flex-1 flex items-center px-4 gap-3 py-2">
              <span className="material-symbols-outlined text-terra-siena">location_on</span>
              <input 
                className="w-full bg-transparent border-none focus:ring-0 text-espresso placeholder-terra-siena/60 py-2 focus:outline-none"
                placeholder={t('homepage.cityPlaceholder')}
                type="text"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                data-testid="search-location-input"
              />
            </div>
            <button 
              type="submit"
              className="bg-primary hover:bg-primary/90 text-espresso font-bold px-8 py-3 rounded-lg transition-all"
              data-testid="search-submit-btn"
            >
              {t('homepage.searchButton')}
            </button>
          </form>
        </div>
      </section>

      {/* Today's Commemorations */}
      <section className="bg-primary/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-full">
              <span className="material-symbols-outlined text-primary text-3xl">volunteer_activism</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-espresso">{t('homepage.todayRemembrance')}</h3>
              <p className="text-terra-siena">{t('homepage.todayRememberanceDesc')}</p>
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-espresso/60 text-lg">{language === 'it' ? 'Oggi,' : language === 'en' ? 'Today,' : language === 'fr' ? "Aujourd'hui," : language === 'de' ? 'Heute,' : 'Hoy,'}</span>
            <span className="text-4xl font-display font-bold text-primary">{stats.total_condoglianze?.toLocaleString() || '1.234'}</span>
            <span className="text-espresso/60 text-lg italic font-light">{t('homepage.todayThoughts')}</span>
          </div>
          <button 
            onClick={() => navigate('/necrologi')}
            className="text-primary font-bold flex items-center gap-2 hover:gap-3 transition-all"
          >
            {t('homepage.sendThought')} <span className="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
      </section>

      {/* Recent Death Notices Grid */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="recent-obituaries">
        <div className="flex justify-between items-end mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-espresso">{t('homepage.recentObituaries')}</h2>
            <div className="w-20 h-1 bg-primary mt-4"></div>
          </div>
          <button 
            onClick={() => navigate('/necrologi')}
            className="text-terra-siena hover:text-primary underline underline-offset-4 font-medium"
            data-testid="view-all-btn"
          >
            {t('homepage.viewAllObituaries')}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <span className="material-symbols-outlined text-6xl text-primary animate-pulse">hourglass_empty</span>
            </div>
          ) : (
            recentObituaries.map((person) => (
              <div 
                key={person.id}
                onClick={() => navigate(getNecrologioUrl(person))}
                className="group cursor-pointer"
                data-testid={`obituary-card-${person.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden rounded-xl border border-primary/30 mb-4 relative">
                  <img loading="lazy" decoding="async" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    style={{ filter: 'sepia(0.4) contrast(0.9) brightness(0.9)' }}
                    src={person.foto_url || 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&q=75&fm=webp&auto=format'}
                    alt={`${person.nome} ${person.cognome}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                <div className="text-center">
                  <h4 className="text-xl font-bold text-espresso mb-1">{person.nome} {person.cognome}</h4>
                  <p className="text-terra-siena font-light tracking-wide italic mb-2">
                    {person.data_nascita ? new Date(person.data_nascita).getFullYear() : '...'} — {new Date(person.data_morte).getFullYear()}
                  </p>
                  <p className="text-sm text-espresso/60">{person.comune}{person.provincia && `, ${person.provincia}`}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Riaccendi una Stella */}
      <section className="relative overflow-hidden" style={{ minHeight: '700px' }} data-testid="riaccendi-stella-section">
        {/* Background Image with parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/f926b72d-b578-4a4e-8f7f-e8d52e778b09/images/ab485856e3c2379727de7141913052afd7955713e90c07be39d83a015d23eec1.png')`,
            backgroundAttachment: 'fixed',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
        </div>

        {/* Animated Lanterns */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={`item-${i}`}
              className="absolute rounded-full"
              style={{
                width: `${8 + Math.random() * 10}px`,
                height: `${10 + Math.random() * 14}px`,
                left: `${5 + (i * 8) + Math.random() * 4}%`,
                bottom: `-20px`,
                background: `radial-gradient(ellipse, rgba(255,${180 + Math.floor(Math.random()*40)},${50 + Math.floor(Math.random()*40)},0.9), rgba(255,${140 + Math.floor(Math.random()*40)},0,0.3))`,
                boxShadow: `0 0 ${15 + Math.random()*15}px rgba(255,180,50,0.5)`,
                animation: `lanternRise ${18 + Math.random() * 14}s linear infinite`,
                animationDelay: `${Math.random() * 16}s`,
              }}
            />
          ))}
        </div>

        {/* Twinkling Stars */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(25)].map((_, i) => (
            <div
              key={`star-${i}`}
              className="absolute rounded-full bg-white"
              style={{
                width: `${1.5 + Math.random() * 2.5}px`,
                height: `${1.5 + Math.random() * 2.5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 40}%`,
                animation: `twinkle ${2 + Math.random() * 4}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: 0,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-28">
          <div className="w-16 h-16 mx-auto mb-8 rounded-full bg-amber-400/10 backdrop-blur-sm border border-amber-300/20 flex items-center justify-center">
            <span className="material-symbols-outlined text-amber-300 text-3xl" style={{ animation: 'twinkle 3s ease-in-out infinite' }}>star</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-8 leading-tight drop-shadow-lg">
            Ogni persona amata è una stella<br className="hidden md:block" /> che merita di brillare per sempre
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6 mb-10">
            <p className="text-lg md:text-xl text-white/90 leading-relaxed drop-shadow">
              Forse sono passati anni, forse decenni. Ma il ricordo di chi hai amato non conosce il tempo.
              Quel sorriso, quella voce, quell'abbraccio vivono ancora dentro di te, intatti come il primo giorno.
            </p>
            <p className="text-lg text-white/80 leading-relaxed drop-shadow">
              Se desideri riaccendere quella luce, puoi farlo. Rivolgiti all'agenzia funebre che ha accompagnato
              il tuo caro nel suo ultimo viaggio, anche se sono trascorsi tanti anni, e chiedi di pubblicare
              il suo necrologio su Resta in Pace. Oppure contatta direttamente noi: ci occuperemo di tutto,
              prendendo contatto con l'agenzia funebre, recuperando le informazioni necessarie e pubblicando
              il ricordo con la massima cura e rispetto.
            </p>
            <p className="text-lg text-white/80 leading-relaxed drop-shadow">
              Con un piccolo contributo, il suo nome tornerà a brillare nel nostro firmamento digitale,
              accanto a tutte le altre stelle che continuano a vivere nel cuore di chi le ama.
            </p>
            <p className="text-amber-200/90 italic text-lg leading-relaxed drop-shadow" style={{ fontFamily: "'Playfair Display', serif" }}>
              Il vostro contributo ci permette di custodire queste stelle, di mantenere vivo questo cielo
              di ricordi dove ogni nome è una luce che non si spegne. Noi faremo la nostra parte per
              preservarne la memoria. Perché chi è stato amato davvero non smette mai di esistere.
            </p>
            <p className="text-lg text-white/85 leading-relaxed drop-shadow">
              Contattaci e lascia che ci prendiamo cura anche di questo gesto d'amore.
              Insieme faremo tornare a splendere una stella che il tempo non ha mai spento.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/agenzie')}
              className="bg-amber-400 text-[#1a0f05] px-8 py-4 rounded-xl font-bold hover:bg-amber-300 transition-all flex items-center gap-3 shadow-lg shadow-amber-400/30 hover:shadow-amber-300/40 hover:scale-105"
              data-testid="riaccendi-stella-cta"
            >
              <span className="material-symbols-outlined">search</span>
              Trova la tua Agenzia
            </button>
            <button
              onClick={() => navigate('/pubblica-necrologio')}
              className="border-2 border-white/25 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10 transition-all flex items-center gap-3 backdrop-blur-sm hover:scale-105"
              data-testid="pubblica-necrologio-cta"
            >
              <span className="material-symbols-outlined">edit</span>
              Pubblica un Necrologio
            </button>
            <button
              onClick={() => navigate('/richiedi-pubblicazione')}
              className="border-2 border-amber-400/40 text-amber-200 px-8 py-4 rounded-xl font-bold hover:bg-amber-400/10 transition-all flex items-center gap-3 backdrop-blur-sm hover:scale-105"
              data-testid="richiedi-pubblicazione-cta"
            >
              <span className="material-symbols-outlined">handshake</span>
              Richiedi la Pubblicazione
            </button>
          </div>
          
          <div className="mt-14 flex items-center justify-center gap-3 text-white/50 text-sm">
            <span className="material-symbols-outlined text-amber-400/60 text-base" style={{ animation: 'twinkle 4s ease-in-out infinite' }}>auto_awesome</span>
            <span className="italic">Ogni stella accesa è un atto d'amore</span>
            <span className="material-symbols-outlined text-amber-400/60 text-base" style={{ animation: 'twinkle 4s ease-in-out infinite 1s' }}>auto_awesome</span>
          </div>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes lanternRise {
            0% { 
              transform: translateY(0) translateX(0) scale(0.4);
              opacity: 0;
            }
            5% {
              opacity: 0.8;
              transform: translateY(-50px) translateX(5px) scale(0.7);
            }
            50% {
              opacity: 0.9;
              transform: translateY(-350px) translateX(-15px) scale(0.9);
            }
            100% { 
              transform: translateY(-750px) translateX(10px) scale(0.3);
              opacity: 0;
            }
          }
          @keyframes twinkle {
            0%, 100% { opacity: 0.15; }
            50% { opacity: 0.9; }
          }
        `}</style>
      </section>

      {/* Anniversari, Trigesimi e Messe in Suffragio Section */}
      <section className="bg-gradient-to-b from-soft-cream/50 to-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-primary/20 text-primary text-xs font-bold uppercase tracking-widest rounded mb-4">
              Commemorazioni
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-charcoal mb-4">
              Anniversari, Trigesimi e Messe in Suffragio
            </h2>
            <p className="text-muted-gold text-lg max-w-2xl mx-auto leading-relaxed">
              Nel silenzio del ricordo, custodiamo con amore la memoria di chi ci ha lasciato. 
              Ogni preghiera è un ponte tra la terra e il cielo, un gesto di fede e speranza.
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Anniversari e Trigesimi Card */}
            <div 
              onClick={() => navigate('/anniversari')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-soft-cream shadow-lg hover:shadow-xl transition-all cursor-pointer"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-bl-full"></div>
              <div className="p-8 relative z-10">
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-3xl text-primary">event</span>
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-3">Anniversari e Trigesimi</h3>
                <p className="text-muted-gold leading-relaxed mb-6">
                  Trova le commemorazioni del trigesimo e gli anniversari della scomparsa dei nostri cari. 
                  Un momento di raccoglimento per onorare chi non c'è più.
                </p>
                <div className="flex items-center text-primary font-semibold group-hover:gap-3 transition-all">
                  <span>Consulta il calendario</span>
                  <span className="material-symbols-outlined ml-2 group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </div>
              </div>
            </div>

            {/* Messe in Suffragio Card */}
            <div 
              onClick={() => navigate('/messe-suffraggio')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-soft-cream shadow-lg hover:shadow-xl transition-all cursor-pointer"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-bl-full"></div>
              <div className="p-8 relative z-10">
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-3xl text-primary">church</span>
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-3">Messe in Suffragio</h3>
                <p className="text-muted-gold leading-relaxed mb-6">
                  Scopri le messe celebrate in memoria dei defunti nella tua zona. 
                  Un'occasione per unirsi in preghiera e condividere il ricordo.
                </p>
                <div className="flex items-center text-primary font-semibold group-hover:gap-3 transition-all">
                  <span>Trova una messa</span>
                  <span className="material-symbols-outlined ml-2 group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Message */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-charcoal/5 px-6 py-3 rounded-full">
              <span className="material-symbols-outlined text-primary">favorite</span>
              <span className="text-sm text-muted-gold italic">
                "Nel ricordo vive chi amiamo, nella preghiera lo accompagniamo"
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Lascito Digitale Promo Banner */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="relative rounded-3xl overflow-hidden min-h-[450px] flex items-center p-8 md:p-16">
          <div 
            className="absolute inset-0 w-full h-full bg-cover"
            style={{ 
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/00ba45b6-fbea-4416-935a-da2a8f87a9b8/images/e98a60d6ac0a3f85ddb87fdbe3f4ad7fca7c100245c16663fb3ba7e3e84adf32.png')`,
              backgroundPosition: 'center 40%',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-espresso/80 via-espresso/50 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-xl text-white">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 mb-6">
              <span className="material-symbols-outlined text-primary text-4xl">history_edu</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">{cms('testamento_title', 'Lascito Digitale')}</h2>
            <p className="text-lg opacity-90 mb-4 leading-relaxed">
              {cms('testamento_desc', 'Le tue ultime volontà, custodite con cura e amore. Affida a chi ami le informazioni sul tuo notaio: saranno al sicuro, pronte ad accompagnare i tuoi cari quando ne avranno bisogno.')}
            </p>
            <p className="text-primary italic font-light text-lg mb-8">
              Un gesto d'amore che dura per sempre.
            </p>
            <button 
              onClick={() => navigate('/testamento-digitale')}
              className="bg-primary text-espresso hover:bg-primary/90 transition-all font-bold px-8 py-4 rounded-xl flex items-center gap-3 shadow-xl hover:scale-105"
              data-testid="will-promo-cta"
            >
              <span className="material-symbols-outlined">favorite</span>
              Proteggi chi ami
            </button>
          </div>
        </div>
      </section>

      {/* Lettere al Cielo - NEW - Con immagine cipressi toscani */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="relative rounded-3xl overflow-hidden min-h-[450px] flex items-center p-8 md:p-16">
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://images.unsplash.com/photo-1708783487757-5cea4c3f7281?w=1280&q=75&fm=webp&auto=format')`,
              filter: 'brightness(0.85) saturate(1.1)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-espresso/60 to-espresso/20"></div>
          </div>
          <div className="relative z-10 max-w-xl text-white">
            <span className="inline-block px-4 py-1 bg-primary text-espresso text-xs font-bold uppercase tracking-widest rounded mb-6">
              Nuovo
            </span>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">Lettere al Cielo</h2>
            <p className="text-lg opacity-90 mb-8 leading-relaxed">
              Come sussurri portati dal vento tra i cipressi, le tue parole raggiungono chi non c'è più. Uno spazio sacro dove i pensieri si trasformano in stelle, dove l'amore diventa eterno.
            </p>
            <button 
              onClick={() => navigate('/lettere-al-cielo')}
              className="bg-primary text-espresso hover:bg-primary/90 transition-colors font-bold px-8 py-4 rounded-xl flex items-center gap-3"
              data-testid="lettere-al-cielo-btn"
            >
              <span className="material-symbols-outlined">edit_note</span>
              Scrivi una lettera
            </button>
          </div>
        </div>
      </section>

      {/* Itinerari della Memoria Section */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="relative rounded-3xl overflow-hidden min-h-[450px] flex items-center p-8 md:p-16">
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1280&q=75&fm=webp&auto=format')`,
              filter: 'sepia(0.4) contrast(0.9) brightness(0.8)'
            }}
          >
            <div className="absolute inset-0 bg-espresso/30"></div>
          </div>
          <div className="relative z-10 max-w-xl text-white">
            <span className="inline-block px-4 py-1 bg-primary text-espresso text-xs font-bold uppercase tracking-widest rounded mb-6">
              Cultura & Storia
            </span>
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">Itinerari della Memoria</h2>
            <p className="text-lg opacity-90 mb-8 leading-relaxed">
              Esplora la storia culturale e artistica dei cimiteri monumentali italiani. Un viaggio tra arte, silenzio e memoria per riscoprire il valore del nostro patrimonio.
            </p>
            <button 
              onClick={() => navigate('/itinerari-memoria')}
              className="bg-white text-espresso hover:bg-primary transition-colors font-bold px-8 py-4 rounded-xl flex items-center gap-3"
              data-testid="discover-itineraries-btn"
            >
              Scopri i percorsi
              <span className="material-symbols-outlined">explore</span>
            </button>
          </div>
        </div>
      </section>

      {/* Support and Guides */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 max-w-2xl mx-auto">
          <div className="bg-white p-10 rounded-3xl border border-terra-siena/10 hover:border-primary/30 transition-all group">
            <div className="text-primary mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-5xl">edit_calendar</span>
            </div>
            <h3 className="text-2xl font-bold text-espresso mb-4">Guida alla Pianificazione</h3>
            <p className="text-espresso/70 mb-8 leading-relaxed">
              Informazioni chiare per organizzare un rito funebre nel rispetto delle volontà, sollevando la famiglia da oneri burocratici e incertezze.
            </p>
            <button 
              onClick={() => navigate('/guide')}
              className="text-primary font-bold flex items-center gap-2 hover:translate-x-2 transition-transform"
            >
              Scopri di più <span className="material-symbols-outlined">arrow_forward</span>
            </button>
          </div>
        </div>
      </section>

      {/* Libro della Memoria Section */}
      <section className="py-20 bg-gradient-to-b from-[#faf5f7] to-white" data-testid="libro-memoria-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#974e73]/10 text-[#974e73] text-xs font-bold uppercase tracking-widest rounded-full mb-4">
                Per le famiglie
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-espresso mb-6 leading-tight">
                Il Libro della Memoria
              </h2>
              <p className="text-terra-siena text-lg leading-relaxed mb-6">
                Un racconto delicato e intimo, costruito con le parole di chi ha voluto bene al tuo caro. 
                Un libro che custodisce per sempre i messaggi, le foto e le candele accese in suo onore.
              </p>
              <p className="text-terra-siena leading-relaxed mb-8">
                Chiedi alla tua agenzia funebre di fiducia di creare il Libro della Memoria: 
                un ricordo tangibile che riunisce tutto l'affetto ricevuto in un unico, prezioso volume da sfogliare 
                ogni volta che sentirai il bisogno di rivivere quei momenti.
              </p>
              <button
                onClick={() => navigate('/libro-della-memoria')}
                className="inline-flex items-center gap-3 bg-[#974e73] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#7d3f5f] transition-all shadow-md hover:shadow-lg"
                data-testid="libro-memoria-cta-btn"
              >
                <span className="material-symbols-outlined">auto_stories</span>
                Scopri il Libro della Memoria
              </button>
              <button
                onClick={() => navigate('/libro-della-memoria/demo')}
                className="inline-flex items-center gap-3 ml-0 sm:ml-3 mt-3 sm:mt-0 border-2 border-[#974e73] text-[#974e73] px-8 py-4 rounded-xl font-bold hover:bg-[#974e73] hover:text-white transition-all"
                data-testid="libro-memoria-demo-cta-btn"
              >
                <span className="material-symbols-outlined">menu_book</span>
                Sfoglia la demo
              </button>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#e7d0db] transform rotate-1 hover:rotate-0 transition-transform">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#974e73] to-[#7d3f5f] rounded-full flex items-center justify-center shadow-lg">
                    <span className="material-symbols-outlined text-white text-3xl">auto_stories</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-espresso text-lg">Un tesoro di ricordi</h3>
                    <p className="text-sm text-terra-siena">Raccolti con cura, custoditi per sempre</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-[#faf5f7] rounded-lg">
                    <span className="material-symbols-outlined text-[#974e73]">message</span>
                    <span className="text-sm text-gray-700">Messaggi di condoglianza</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#faf5f7] rounded-lg">
                    <span className="material-symbols-outlined text-[#974e73]">photo_library</span>
                    <span className="text-sm text-gray-700">Foto e immagini condivise</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#faf5f7] rounded-lg">
                    <span className="material-symbols-outlined text-[#974e73]">local_fire_department</span>
                    <span className="text-sm text-gray-700">Candele accese con dediche</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#faf5f7] rounded-lg">
                    <span className="material-symbols-outlined text-[#974e73]">print</span>
                    <span className="text-sm text-gray-700">Stampabile in alta qualità</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest rounded mb-4">
            Nuovo
          </span>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-charcoal mb-4">Podcast: Ascolta, Rifletti, Guarisci</h2>
          <p className="text-terra-siena max-w-2xl mx-auto">
            Episodi dedicati al supporto nel lutto, storie di resilienza e consigli pratici per affrontare i momenti difficili.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {[
            { icon: 'psychology', title: 'Supporto al Lutto', desc: 'Consigli di psicologi esperti', color: 'blue' },
            { icon: 'auto_stories', title: 'Storie di Resilienza', desc: 'Testimonianze di speranza', color: 'amber' },
            { icon: 'gavel', title: 'Consigli Legali', desc: 'Aspetti pratici e burocratici', color: 'green' }
          ].map((item, i) => (
            <div key={`item-${i}`} className="bg-white rounded-2xl p-6 border border-soft-cream hover:shadow-lg transition-all group">
              <div className={`w-14 h-14 rounded-full mb-4 flex items-center justify-center ${
                item.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                item.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                'bg-green-100 text-green-600'
              } group-hover:scale-110 transition-transform`}>
                <span className="material-symbols-outlined text-2xl">{item.icon}</span>
              </div>
              <h3 className="text-lg font-bold text-charcoal mb-2">{item.title}</h3>
              <p className="text-sm text-terra-siena">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/podcast')}
            className="inline-flex items-center gap-3 bg-primary text-espresso px-8 py-4 rounded-xl font-bold hover:bg-primary/90 transition-all shadow-md hover:shadow-lg"
            data-testid="podcast-cta-btn"
          >
            <span className="material-symbols-outlined">podcasts</span>
            Ascolta i Podcast
          </button>

          <p className="text-xs text-terra-siena mt-6 mb-3 uppercase tracking-widest font-semibold">Disponibile su</p>
          <PodcastBadges />
        </div>
      </section>

      {/* CTA Section - Agencies */}
      <section className="py-16 bg-espresso text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold mb-6">Sei un'impresa di onoranze funebri?</h3>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Unisciti al network di Resta in Pace per offrire alle famiglie un servizio digitale moderno, rispettoso e di alta qualità.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/partner')}
              className="bg-primary text-espresso px-8 py-4 rounded-lg font-bold hover:bg-primary/90 transition-all"
              data-testid="become-partner-btn"
            >
              DIVENTA PARTNER
            </button>
            <button
              onClick={() => navigate('/agenzie')}
              className="border-2 border-white/20 text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-all"
            >
              MAGGIORI INFORMAZIONI
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
